/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Upload,
  Download,
  Trash2,
  FileText,
  Loader2,
  AlertCircle,
  Cloud,
  Eye,
  X,
  CheckCircle2,
} from 'lucide-react'
import { toast } from 'sonner'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { EmptyState } from '@/components/empty-state'
import { SettingsSection } from '../components/settings-section'
import {
  getDocuments,
  uploadDocumentChunked,
  deleteDocument,
  getStreamUrl,
  getPreviewUrl,
} from '../../docs/api'
import type { ChunkUploadProgress } from '../../docs/api'
import type { Document } from '../../docs/types'
import { PreviewDialog, isPreviewable } from './preview-dialog'

// ============================================================================
// Helpers
// ============================================================================

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const k = 1024
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  const value = bytes / Math.pow(k, i)
  return `${value.toFixed(i > 0 ? 1 : 0)} ${units[i]}`
}

function formatTime(timestamp: number): string {
  if (!timestamp) return '-'
  return new Date(timestamp * 1000).toLocaleString()
}

// ============================================================================
// 上传任务
// ============================================================================

interface UploadTask {
  id: string
  file: File
  name: string
  desc: string
  progress: ChunkUploadProgress
  abortController: AbortController
}

// ============================================================================
// Document Upload Section
// ============================================================================

export function DocumentUploadSection() {
  const { t } = useTranslation()
  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const [deleteTarget, setDeleteTarget] = useState<Document | null>(null)
  const [deleting, setDeleting] = useState(false)

  // 上传状态 - 多文件队列
  const [uploadTasks, setUploadTasks] = useState<UploadTask[]>([])
  const [showUpload, setShowUpload] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 预览状态
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState('')
  const [previewName, setPreviewName] = useState('')
  const [previewStreamUrl, setPreviewStreamUrl] = useState('')

  const pageSize = 5

  const fetchDocuments = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getDocuments(page, pageSize)
      if (res.success) {
        setDocuments(res.data || [])
        setTotal(res.total || 0)
      } else {
        setError(res.message || t('load_failed'))
      }
    } catch {
      setError(t('load_failed'))
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, t])

  useEffect(() => {
    fetchDocuments()
  }, [fetchDocuments])

  // 拖拽处理
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFilesToQueue(Array.from(e.dataTransfer.files))
    }
  }, [])

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        addFilesToQueue(Array.from(e.target.files))
        // 重置 input 以便能重新选择相同的文件
        e.target.value = ''
      }
    },
    []
  )

  // 添加文件到上传队列
  const addFilesToQueue = useCallback((files: File[]) => {
    const newTasks: UploadTask[] = files.map((file) => ({
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      file,
      name: file.name.replace(/\.[^.]+$/, ''),
      desc: '',
      progress: {
        uploadId: '',
        fileName: file.name,
        totalChunks: Math.ceil(file.size / (5 * 1024 * 1024)),
        uploadedChunks: 0,
        status: 'pending' as const,
      },
      abortController: new AbortController(),
    }))
    setUploadTasks((prev) => [...prev, ...newTasks])

    // 自动开始上传
    newTasks.forEach((task) => {
      uploadSingleFile(task)
    })
  }, [])

  // 上传单个文件（分片 + 断点续传）
  const uploadSingleFile = async (task: UploadTask) => {
    const updateProgress = (progress: ChunkUploadProgress) => {
      setUploadTasks((prev) =>
        prev.map((t) => (t.id === task.id ? { ...t, progress } : t))
      )
    }

    try {
      const res = await uploadDocumentChunked(
        task.file,
        task.name,
        task.desc,
        updateProgress
      )
      if (res.success) {
        toast.success(`「${task.name}」上传成功`)
        // 从队列中移除（done 状态）
        setUploadTasks((prev) => prev.filter((t) => t.id !== task.id))
        fetchDocuments()
      } else {
        toast.error(`「${task.name}」上传失败: ${res.message}`)
        updateProgress({ ...task.progress, status: 'error', error: res.message })
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '上传失败'
      toast.error(`「${task.name}」: ${msg}`)
      updateProgress({ ...task.progress, status: 'error', error: msg })
    }
  }

  // 取消上传
  const cancelUpload = (taskId: string) => {
    setUploadTasks((prev) =>
      prev.map((t) => {
        if (t.id === taskId) {
          t.abortController.abort()
        }
        return t
      })
    )
    // 移除完成和失败的任务
    setUploadTasks((prev) => prev.filter((t) => t.id !== taskId))
  }

  // 清理已完成的
  const clearDone = () => {
    setUploadTasks((prev) => prev.filter((t) => t.progress.status !== 'done' && t.progress.status !== 'error'))
    setShowUpload(false)
  }

  // Is any upload in progress?
  const isUploading = uploadTasks.some((t) =>
    ['pending', 'uploading', 'merging'].includes(t.progress.status)
  )

  // 删除处理
  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      const res = await deleteDocument(deleteTarget.id)
      if (res.success) {
        toast.success(t('delete') + ' ' + t('success'))
        setDeleteTarget(null)
        if (documents.length === 1 && page > 0) {
          setPage(page - 1)
        } else {
          fetchDocuments()
        }
      } else {
        toast.error(res.message || t('delete') + ' ' + t('failed'))
      }
    } catch {
      toast.error(t('delete') + ' ' + t('failed'))
    } finally {
      setDeleting(false)
    }
  }

  // 预览处理
  const handlePreview = async (doc: Document) => {
    setPreviewName(doc.name || doc.file_name)
    setPreviewOpen(true)
    setPreviewLoading(true)
    setPreviewUrl('')
    setPreviewStreamUrl(getStreamUrl(doc.id))
    try {
      const res = await getPreviewUrl(doc.id)
      // 预览接口返回扁平结构: { success, url, name, type }
      const url = (res as unknown as Record<string, unknown>).url as string | undefined
      if (res.success && url) {
        setPreviewUrl(url)
      } else {
        toast.error(res.message || '获取预览链接失败')
        setPreviewOpen(false)
      }
    } catch {
      toast.error('获取预览链接失败')
      setPreviewOpen(false)
    } finally {
      setPreviewLoading(false)
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <SettingsSection
      title={t('文档上传') || '文档上传'}
      description={t('上传文档文件到腾讯云对象存储（COS），支持下载和删除管理')}
    >
      {/* COS 提示 */}
      <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50/50 p-4">
        <Cloud className="mt-0.5 h-5 w-5 shrink-0 text-blue-500" />
        <div className="text-sm">
          <p className="font-medium text-blue-700">腾讯云 COS 对象存储</p>
          <p className="mt-0.5 text-blue-600/80">
            上传的文件将存储在腾讯云广州地域的对象存储桶中，提供高可用、低延迟的文件访问服务。
          </p>
        </div>
      </div>

      {/* 上传区域 */}
      {!showUpload ? (
        <button
          onClick={() => setShowUpload(true)}
          className="inline-flex items-center gap-2 self-start rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <Upload className="h-4 w-4" />
          {t('upload')} {t('document')}
        </button>
      ) : (
        <div className="space-y-4 rounded-xl border p-5">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">
              上传文档（支持多选 · 断点续传）
            </h4>
            <button
              onClick={clearDone}
              disabled={isUploading}
              className="text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
            >
              关闭
            </button>
          </div>

          {/* 拖拽区域 */}
          <div
            className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors ${
              dragActive
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-primary/50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileChange}
            />
            <Upload className="mb-2 h-10 w-10 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              点击或拖拽文件到此处（支持多文件）
            </span>
            <span className="mt-1 text-xs text-muted-foreground/50">
              支持 Office 文档、PDF、图片等，单文件最大 2GB
            </span>
          </div>

          {/* 上传队列 */}
          {uploadTasks.length > 0 && (
            <div className="space-y-2">
              <span className="text-xs font-medium text-muted-foreground">
                上传队列 ({uploadTasks.length} 个文件)
              </span>
              <div className="max-h-[400px] space-y-2 overflow-y-auto">
                {uploadTasks.map((task) => {
                  const { progress: p } = task
                  const pct =
                    p.totalChunks > 0
                      ? Math.round((p.uploadedChunks / p.totalChunks) * 100)
                      : 0
                  const isDone = p.status === 'done'
                  const isError = p.status === 'error'
                  const isActive = ['uploading', 'merging', 'pending'].includes(
                    p.status
                  )

                  return (
                    <div
                      key={task.id}
                      className={`flex items-center gap-3 rounded-lg border p-3 ${
                        isError ? 'border-destructive/30 bg-destructive/5' : ''
                      }`}
                    >
                      <FileText
                        className={`h-8 w-8 shrink-0 ${
                          isError
                            ? 'text-destructive'
                            : isDone
                            ? 'text-green-500'
                            : 'text-primary'
                        }`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={task.name}
                            onChange={(e) => {
                              setUploadTasks((prev) =>
                                prev.map((t) =>
                                  t.id === task.id
                                    ? { ...t, name: e.target.value }
                                    : t
                                )
                              )
                            }}
                            disabled={!isActive}
                            className="min-w-0 flex-1 bg-transparent text-sm font-medium outline-none disabled:opacity-60"
                            placeholder="文档名称"
                          />
                          {isDone && (
                            <CheckCircle2 className="h-4 w-4 shrink-0 text-green-500" />
                          )}
                        </div>
                        <p className="truncate text-xs text-muted-foreground">
                          {task.file.name}
                        </p>
                        <div className="mt-1.5">
                          {/* 进度条 */}
                          <div className="flex items-center gap-2">
                            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                              <div
                                className={`h-full rounded-full transition-all duration-300 ${
                                  isError
                                    ? 'bg-destructive'
                                    : isDone
                                    ? 'bg-green-500'
                                    : 'bg-primary'
                                }`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                              {pct}%
                            </span>
                          </div>
                          <p className="mt-0.5 text-xs text-muted-foreground/60">
                            {p.status === 'pending'
                              ? '等待上传...'
                              : p.status === 'uploading'
                              ? `分片上传中 (${p.uploadedChunks}/${p.totalChunks})`
                              : p.status === 'merging'
                              ? '合并分片中...'
                              : p.status === 'done'
                              ? '上传完成'
                              : isError
                              ? p.error || '上传失败'
                              : ''}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => cancelUpload(task.id)}
                        className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                        title="取消"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 错误状态 */}
      {error && !loading && (
        <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-12">
          <AlertCircle className="h-10 w-10 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
          <button
            onClick={fetchDocuments}
            className="rounded-lg border px-4 py-2 text-sm font-medium transition-colors hover:bg-muted"
          >
            {t('retry') || '重试'}
          </button>
        </div>
      )}

      {/* 加载中 */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* 空状态 */}
      {!loading && !error && documents.length === 0 && (
        <EmptyState
          icon={FileText}
          title={t('no_documents') || '暂无文档'}
          description={t('no_documents_desc') || '点击上方按钮上传文档到服务器本地存储'}
        />
      )}

      {/* 文档列表 */}
      {!loading && !error && documents.length > 0 && (
        <div className="overflow-hidden rounded-xl border">
          {/* 表头 */}
          <div className="hidden border-b bg-muted/50 px-4 py-3 sm:grid sm:grid-cols-[1fr_70px_120px_180px_140px]">
            <span className="text-xs font-medium text-muted-foreground">
              文件名称
            </span>
            <span className="text-xs font-medium text-muted-foreground">
              预览
            </span>
            <span className="text-xs font-medium text-muted-foreground">
              文件大小
            </span>
            <span className="text-xs font-medium text-muted-foreground">
              上传时间
            </span>
            <span className="text-xs font-medium text-muted-foreground">
              操作
            </span>
          </div>

          {/* 列表行 */}
          <div className="divide-y">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="flex flex-col gap-2 px-4 py-3 transition-colors hover:bg-muted/30 sm:grid sm:grid-cols-[1fr_70px_120px_180px_140px] sm:gap-0 sm:py-3.5"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{doc.name}</p>
                  {doc.description && (
                    <p className="truncate text-xs text-muted-foreground">
                      {doc.description}
                    </p>
                  )}
                  <p className="mt-0.5 text-xs text-muted-foreground sm:hidden">
                    {doc.file_name} · {formatFileSize(doc.file_size)}
                  </p>
                </div>
                {/* 预览按钮 */}
                <div className="flex items-center sm:flex hidden">
                  {isPreviewable(doc.file_name) ? (
                    <button
                      onClick={() => handlePreview(doc)}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
                      title="预览"
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    <span className="text-xs text-muted-foreground/40">-</span>
                  )}
                </div>
                <div className="hidden items-center text-sm text-muted-foreground sm:flex">
                  {formatFileSize(doc.file_size)}
                </div>
                <div className="hidden items-center text-sm text-muted-foreground sm:flex">
                  {formatTime(doc.created_time)}
                </div>
                <div className="flex items-center gap-1 sm:gap-2">
                  {isPreviewable(doc.file_name) && (
                    <button
                      onClick={() => handlePreview(doc)}
                      className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/10 sm:hidden"
                      title="预览"
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <a
                    href={getStreamUrl(doc.id)}
                    className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    title={t('download')}
                  >
                    <Download className="h-3.5 w-3.5" />
                    <span className="sm:hidden">{t('download')}</span>
                  </a>
                  <button
                    onClick={() => setDeleteTarget(doc)}
                    className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-destructive/80 transition-colors hover:bg-destructive/10 hover:text-destructive"
                    title={t('delete')}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    <span className="sm:hidden">{t('delete')}</span>
                  </button>
                  <span className="ml-auto text-xs text-muted-foreground sm:hidden">
                    {formatTime(doc.created_time)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* 分页 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t px-4 py-3">
              <span className="text-xs text-muted-foreground">
                共 {total} 条 · 第 {page + 1}/{totalPages} 页
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted disabled:opacity-40"
                >
                  上一页
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                  className="rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted disabled:opacity-40"
                >
                  下一页
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 删除确认 */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
        title={t('delete_confirm_title') || '确认删除'}
        desc={
          (t('delete_confirm_desc') || '确定要删除文档 "{name}" 吗？此操作不可撤销。').replace(
            '{name}',
            deleteTarget?.name || ''
          )
        }
        confirmText={t('delete')}
        destructive
        isLoading={deleting}
        handleConfirm={handleDelete}
      />

      {/* 预览弹窗 */}
      <PreviewDialog
        open={previewOpen}
        fileName={previewName}
        previewUrl={previewUrl}
        streamUrl={previewStreamUrl}
        loading={previewLoading}
        onClose={() => {
          setPreviewOpen(false)
          setPreviewUrl('')
          setPreviewStreamUrl('')
        }}
      />
    </SettingsSection>
  )
}
